import { useState } from "react";
import { supabase } from "../lib/supabase";
import { getUserHouseholdId } from "../services/householdService";
import { useQuery, useQueryClient } from "./useDataCache";

// Stable query key used by other hooks/components to invalidate the household ID cache
export const USER_HOUSEHOLD_ID_KEY = ["userHouseholdId"] as const;

// Module-level cache of the last successfully fetched household ID.
// Lives outside React so it is never affected by render cycles, effect timing,
// or React Query error states. Only written inside queryFn (not during render)
// on a confirmed successful fetch, and cleared explicitly on sign-out.
let _lastKnownHouseholdId: string | null = null;

export function clearLastKnownHouseholdId() {
  _lastKnownHouseholdId = null;
}

// Hook to load the current user's household ID and expose loading state.
//
// Design contract:
//   - The household ID, once successfully fetched, is NEVER cleared by a
//     failed or in-flight re-fetch. It can only be wiped via
//     clearLastKnownHouseholdId() (called on sign-out / leave-household).
//   - networkMode "offlineFirst" means the query pauses while offline instead
//     of failing, so retries are not exhausted against a dead network.
export function useUser() {
  const queryClient = useQueryClient();

  // Seed the module-level cache from the IDB-persisted React Query cache.
  // useState initializer runs exactly once per mount — safe to read query
  // client and write to module-level state here since it's not a re-render.
  useState(() => {
    if (!_lastKnownHouseholdId) {
      const persisted = queryClient.getQueryData<string | null>(
        USER_HOUSEHOLD_ID_KEY,
      );
      if (persisted) {
        _lastKnownHouseholdId = persisted;
      } else {
        // Also check the ["household", <id>] cache in case the ID query was
        // evicted but the full household record is still present.
        const householdQueries = queryClient.getQueriesData<{ id: string }>({
          queryKey: ["household"],
        });
        for (const [, data] of householdQueries) {
          if (data?.id) {
            _lastKnownHouseholdId = data.id;
            break;
          }
        }
      }
    }
  });

  const {
    data: fetchedHouseholdId,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: USER_HOUSEHOLD_ID_KEY,
    queryFn: async ({ signal: parentSignal }) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      parentSignal?.addEventListener("abort", () => controller.abort());

      try {
        const fetchedId = await getUserHouseholdId(controller.signal);
        clearTimeout(timeoutId);
        // If the user has explicitly selected an active household, respect it.
        // Fall back to the DB-fetched ID if there is no override.
        const override = localStorage.getItem("pantry_active_household_id");
        const id = override ?? fetchedId;
        // Write to the module-level cache on every successful fetch.
        // queryFn runs outside of render so this is safe.
        if (id) _lastKnownHouseholdId = id;
        return id;
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    },
    // Pause while offline instead of burning through retries against a dead
    // network, which would end in an error state that clears the cached value.
    networkMode: "offlineFirst",
    retry: 3,
    retryDelay: (attempt) => Math.min(attempt * 1000, 5000),
  });

  // The authoritative household ID for the rest of the app:
  //   1. Use the freshly fetched value if available.
  //   2. Fall back to the module-level last-known-good cache.
  //   3. Only null if we have truly never successfully fetched an ID.
  const householdId = fetchedHouseholdId ?? _lastKnownHouseholdId;

  const { data: userId = null } = useQuery({
    queryKey: ["userId"],
    queryFn: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      return session?.user?.id ?? null;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  return {
    householdId,
    userId,
    // isLoading is only true when there is genuinely no ID anywhere — not
    // during background re-fetches where we already have a good cached value.
    isLoading: isLoading && !householdId,
    isFetching,
  };
}
